/**
 * Multi-client test visualization.
 *
 * A `clients: N` defineSandboxTest boots its own harness with per-client render
 * surfaces. So the live preview SHOWS what the test is doing — identical to the
 * "+ Add Client" split-screen — we mount those surfaces using the SAME shared
 * layout utilities (`calculateSplitScreenGrid` / `applySplitScreenLayout`) and
 * the SAME `sandbox-canvas` / `sandbox-canvas-wrapper` / `sandbox-client-toolbar`
 * CSS classes the real add-client path uses. A test step that disconnects a
 * client removes its window and re-flows the split-screen, just like closing a
 * client window in the UI.
 */
import { calculateSplitScreenGrid, applySplitScreenLayout } from '@gotti/test-utils';

export const TEST_VIZ_OVERLAY_ID = 'test-visualization-overlay';

export interface TestVizSurface {
  canvas: HTMLCanvasElement;
  container: HTMLElement;
}

export interface TestVizController {
  /** Remove a client's window and re-flow the split-screen (UI close-window). */
  removeClient(index: number): void;
}

export function clearTestVisualization(): void {
  if (typeof document === 'undefined') return;
  document.getElementById(TEST_VIZ_OVERLAY_ID)?.remove();
}

export function mountTestVisualization(surfaces: TestVizSurface[]): TestVizController | null {
  if (typeof document === 'undefined') return null;
  // Each test boot replaces the overlay; single-client boots just clear it.
  clearTestVisualization();
  if (surfaces.length < 2) return null;
  const host = document.getElementById('canvas-container');
  if (!host) return null;

  const overlay = document.createElement('div');
  overlay.id = TEST_VIZ_OVERLAY_ID;
  overlay.style.position = 'absolute';
  overlay.style.inset = '0';
  overlay.style.zIndex = '30';
  overlay.style.background = '#000';

  // index → its wrapper, so a disconnect can pull one window and re-flow the rest.
  const wrappers = new Map<number, HTMLDivElement>();
  // Optimal split-screen reflow using the SAME layout math add-client uses.
  const reflow = (): void => {
    applySplitScreenLayout({
      container: overlay,
      grid: calculateSplitScreenGrid({
        count: Math.max(1, wrappers.size),
        containerWidth: host.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : 800),
        containerHeight: host.clientHeight || (typeof window !== 'undefined' ? window.innerHeight : 600),
      }),
    });
  };

  surfaces.forEach((s, i) => {
    // SAME wrapper / canvas / toolbar classes as the real "+ Add Client" path so
    // the existing sandbox CSS styles each client window identically.
    const wrapper = document.createElement('div');
    wrapper.className = 'sandbox-canvas-wrapper';
    wrapper.dataset.testVizClient = `player-${i + 1}`;
    wrapper.style.background = '#000';
    wrapper.style.display = 'flex';
    wrapper.style.flexDirection = 'column';
    wrapper.style.alignItems = 'stretch';
    wrapper.style.overflow = 'hidden';

    const toolbar = document.createElement('div');
    toolbar.className = 'sandbox-client-toolbar';
    toolbar.textContent = `player-${i + 1}`;

    s.canvas.classList.add('sandbox-canvas');
    s.canvas.style.flex = '1';
    s.canvas.style.width = '100%';
    s.canvas.style.height = '100%';
    s.canvas.style.objectFit = 'contain';

    wrapper.appendChild(toolbar);
    wrapper.appendChild(s.canvas);
    overlay.appendChild(wrapper);
    wrappers.set(i, wrapper);
  });

  host.appendChild(overlay);
  reflow();

  return {
    removeClient(index: number): void {
      const w = wrappers.get(index);
      if (!w) return;
      w.remove();
      wrappers.delete(index);
      if (wrappers.size === 0) clearTestVisualization();
      else reflow();
    },
  };
}
